import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, gbp, fmtDate, Loading } from "@/components/ui-bits";
import {
  ArrowLeft, BedDouble, Bath, MapPin, Plus, X, Home, FileText, Wrench,
  ShieldCheck, MessageSquare, Landmark, KeyRound, Scale, Dna, Image as ImageIcon,
  Upload, Star, Trash2, Loader2, ChevronLeft, ChevronRight, FolderOpen, Download, ClipboardList,
  ChevronDown, Sheet, Paperclip, CalendarClock, TrendingUp, CheckCircle2, AlertTriangle, PiggyBank, Users, UserPlus, Pencil, History,
} from "lucide-react";
import { toast } from "sonner";
import JSZip from "jszip";
import OpenBankingCard from "@/components/OpenBankingCard";
import CredentialsDialog from "@/components/CredentialsDialog";

const catIcon = {
  Ownership: KeyRound, Tenancy: Home, Rent: Landmark, Maintenance: Wrench,
  Compliance: ShieldCheck, Communication: MessageSquare, Document: FileText,
  Legal: Scale, Refurbishment: Wrench,
};

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "dna");
  const [open, setOpen] = useState(false);
  const [evt, setEvt] = useState({ category: "Communication", title: "", description: "" });
  const [photos, setPhotos] = useState([]);
  const [selectPhotos, setSelectPhotos] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState(() => new Set());
  const [downloadingPhotos, setDownloadingPhotos] = useState(false);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docLabel, setDocLabel] = useState("General Document");
  const [docExpiry, setDocExpiry] = useState("");
  const docNeedsExpiry = /certificate|epc|eicr|licen[cs]e|insurance/i.test(docLabel);
  const fileRef = useRef(null);
  const docRef = useRef(null);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", assignee: "You", priority: "Medium", due_date: "" });
  const [taskFiles, setTaskFiles] = useState([]);
  const [savingTask, setSavingTask] = useState(false);
  const taskFileRef = useRef(null);
  const [taskAttachments, setTaskAttachments] = useState({});
  const [range, setRange] = useState({ from: "", to: "" });
  const [dlOpen, setDlOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ type: "Rent", description: "", amount: "", direction: "in", date: "", awaiting: false, settle_txn_id: "", customType: "" });
  const [allocAmts, setAllocAmts] = useState({});
  const [stmtFiles, setStmtFiles] = useState([]);
  const [uploadingStmt, setUploadingStmt] = useState(false);
  const stmtRef = useRef(null);
  const [payFile, setPayFile] = useState(null);
  const [rentStatus, setRentStatus] = useState(null);
  const [rentForm, setRentForm] = useState({ tenancy_start: "", rent_last_increased: "", tenant: "", tenant_email: "", tenant_phone: "", rent_pcm: "" });
  const [savingRent, setSavingRent] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantOpen, setTenantOpen] = useState(false);
  const [isEditingTenant, setIsEditingTenant] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [endDate, setEndDate] = useState("");
  const [jointOpen, setJointOpen] = useState(false);
  const [jointForm, setJointForm] = useState({ name: "", email: "", phone: "" });
  const [tenancyBusy, setTenancyBusy] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [histEntry, setHistEntry] = useState(null);
  const [histDetail, setHistDetail] = useState(null);
  const [refHistory, setRefHistory] = useState([]);
  const [creds, setCreds] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);

  const loadRentStatus = () => api.get(`/properties/${id}/rent-status`).then(({ data }) => setRentStatus(data)).catch(() => {});
  const loadTenantInfo = () => api.get(`/properties/${id}/tenant`).then(({ data }) => setTenantInfo(data)).catch(() => {});
  const loadRefHistory = () => api.get(`/properties/${id}/referencing-history`).then(({ data }) => setRefHistory(data)).catch(() => {});

  const load = () => api.get(`/properties/${id}`).then(({ data }) => setData(data));
  const loadPhotos = () => api.get(`/files?entity_type=property&entity_id=${id}&bucket=photos`).then(({ data }) => setPhotos(data)).catch(() => {});
  const loadDocs = () => api.get(`/files?entity_type=property&entity_id=${id}&bucket=documents`).then(({ data }) => setDocs(data)).catch(() => {});
  const loadStmtFiles = () => api.get(`/files?entity_type=property&entity_id=${id}&bucket=statement`).then(({ data }) => setStmtFiles(data)).catch(() => {});
  useEffect(() => { load(); loadPhotos(); loadDocs(); loadStmtFiles(); loadRentStatus(); loadRefHistory(); }, [id]);

  const deleteTxn = async (t) => {
    if (!window.confirm(`Delete this ${t.type} entry (${gbp(t.amount)})? This cannot be undone.`)) return;
    try {
      await api.delete(`/transactions/${t.txn_id}`);
      toast.success("Payment deleted");
      await load(); loadRentStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete payment"); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("entity_type", "property");
        fd.append("entity_id", id);
        fd.append("bucket", "photos");
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} image${files.length > 1 ? "s" : ""} uploaded`);
      await load();
      await loadPhotos();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onUploadDoc = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (/certificate|epc|eicr|licen[cs]e|insurance/i.test(docLabel) && !docExpiry) {
      toast.error("Please enter the expiry date before uploading this certificate");
      if (docRef.current) docRef.current.value = "";
      return;
    }
    setUploadingDoc(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("entity_type", "property");
        fd.append("entity_id", id);
        fd.append("bucket", "documents");
        fd.append("label", docLabel);
        if (docExpiry) fd.append("expiry_date", docExpiry);
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
      setDocExpiry("");
      await loadDocs();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingDoc(false);
      if (docRef.current) docRef.current.value = "";
    }
  };

  const removeDoc = async (fid) => {
    await api.delete(`/files/${fid}`);
    toast.success("File removed");
    await loadDocs();
  };

  const updateDocExpiry = async (fid, date) => {
    await api.patch(`/files/${fid}/expiry`, { expiry_date: date || null });
    setDocs((prev) => prev.map((f) => f.file_id === fid ? { ...f, expiry_date: date || null } : f));
    toast.success(date ? "Expiry date saved" : "Expiry cleared");
  };

  const setCover = async (fid) => {
    await api.patch(`/properties/${id}/cover`, { file_id: fid });
    toast.success("Cover image updated");
    load();
  };

  const removePhoto = async (fid) => {
    await api.delete(`/files/${fid}`);
    toast.success("Image removed");
    await load();
    await loadPhotos();
  };

  const movePhoto = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= photos.length) return;
    const arr = [...photos];
    [arr[index], arr[next]] = [arr[next], arr[index]];
    setPhotos(arr);
    await api.post("/files/reorder", { entity_type: "property", entity_id: id, order: arr.map((f) => f.file_id) });
    toast.success("Photo order updated");
  };

  const togglePhotoSelect = (fid) => {
    setSelectedPhotos((prev) => {
      const n = new Set(prev);
      n.has(fid) ? n.delete(fid) : n.add(fid);
      return n;
    });
  };

  const extFor = (f) => {
    const name = f.original_filename || "";
    if (name.includes(".")) return name.split(".").pop();
    const ct = f.content_type || "";
    return ct.split("/")[1] || (f.category === "video" ? "mp4" : "jpg");
  };

  const downloadPhotos = async (list) => {
    if (!list.length) { toast.error("No photos selected"); return; }
    setDownloadingPhotos(true);
    try {
      if (list.length === 1) {
        const f = list[0];
        const res = await fetch(fileUrl(f.file_id));
        if (!res.ok) throw new Error("fetch failed");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = f.original_filename || `${p.reference || "photo"}.${extFor(f)}`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      } else {
        const zip = new JSZip();
        let idx = 1;
        for (const f of list) {
          const res = await fetch(fileUrl(f.file_id));
          if (!res.ok) continue;
          const blob = await res.blob();
          const base = f.original_filename && f.original_filename.includes(".")
            ? f.original_filename
            : `${String(idx).padStart(2, "0")}.${extFor(f)}`;
          zip.file(`${String(idx).padStart(2, "0")}-${base}`, blob);
          idx++;
        }
        const content = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(content);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${p.reference || "property"}-photos.zip`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
      toast.success(`Downloaded ${list.length} photo${list.length > 1 ? "s" : ""}`);
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloadingPhotos(false);
    }
  };

  const addTask = async (e) => {
    e.preventDefault();
    setSavingTask(true);
    try {
      const { data: task } = await api.post("/tasks", { ...taskForm, property_ref: p.reference });
      for (const f of taskFiles) {
        const fd = new FormData();
        fd.append("entity_type", "task");
        fd.append("entity_id", task.task_id);
        fd.append("bucket", "attachments");
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(taskFiles.length ? `Task added with ${taskFiles.length} attachment(s)` : "Task added to this property");
      setTaskOpen(false);
      setTaskForm({ title: "", assignee: "You", priority: "Medium", due_date: "" });
      setTaskFiles([]);
      await load();
    } catch {
      toast.error("Could not add task");
    } finally {
      setSavingTask(false);
    }
  };

  const loadTaskAttachments = async (tasks) => {
    const entries = await Promise.all(
      tasks.map(async (t) => {
        try {
          const { data } = await api.get(`/files?entity_type=task&entity_id=${t.task_id}`);
          return [t.task_id, data];
        } catch {
          return [t.task_id, []];
        }
      })
    );
    setTaskAttachments(Object.fromEntries(entries));
  };

  useEffect(() => {
    if (data?.tasks?.length) loadTaskAttachments(data.tasks);
  }, [data]);

  const downloadStatement = async (format = "pdf") => {
    try {
      const params = { format };
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      const res = await api.get(`/properties/${id}/statement`, { params, responseType: "blob" });
      const warn = res.headers["x-bank-warning"];
      if (warn) toast.warning(decodeURIComponent(warn), { duration: 9000 });
      const ext = format === "xlsx" ? "xlsx" : format === "pdf" ? "pdf" : "csv";
      let fname = `Statement.${ext}`;
      const cd = res.headers["content-disposition"] || res.headers["Content-Disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      if (m) fname = decodeURIComponent(m[1]);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Statement downloaded (${ext.toUpperCase()})`);
      setDlOpen(false);
    } catch {
      toast.error("Could not generate statement");
    }
  };

  const inRange = (d) => {
    const dt = d.slice(0, 10);
    if (range.from && dt < range.from) return false;
    if (range.to && dt > range.to) return false;
    return true;
  };

  const openPay = (awaiting) => {
    setPayForm({ type: awaiting ? "Maintenance" : "Rent", description: "", amount: "",
                 direction: awaiting ? "out" : "in", date: "", awaiting: !!awaiting, settle_txn_id: "", customType: "" });
    setAllocAmts({});
    setPayFile(null);
    setPayOpen(true);
  };

  const OUT_TYPES = ["Maintenance", "Council Tax", "Insurance", "Licence", "Admin Fee", "Landlord Payment", "Contractor Payment"];
  const onTypeChange = (type) => {
    const dir = type === "Rent" || type === "Deposit" ? "in" : (OUT_TYPES.includes(type) ? "out" : payForm.direction);
    setPayForm({ ...payForm, type, direction: dir });
  };

  const addPayment = async (e) => {
    e.preventDefault();
    // Allocate Payment mode: settle (fully or partially) awaiting payments against the available balance
    if (payForm.type === "Allocate Payment") {
      const entries = Object.entries(allocAmts)
        .map(([id, v]) => ({ id, amt: parseFloat(v) || 0 }))
        .filter((e) => e.amt > 0);
      if (entries.length === 0) { toast.error("Enter an amount to allocate against at least one payment"); return; }
      const total = entries.reduce((a, e) => a + e.amt, 0);
      if (total > availableBalance + 0.001) { toast.error("Allocation exceeds the available balance"); return; }
      for (const e of entries) {
        const t = (data.transactions || []).find((x) => x.txn_id === e.id);
        if (!t) continue;
        await api.post("/transactions", { property_ref: p.reference, type: t.type, description: t.description,
          amount: e.amt, direction: t.direction, settle_txn_id: t.txn_id, date: payForm.date || undefined });
      }
      toast.success(`Allocated ${gbp(total)} across ${entries.length} payment${entries.length > 1 ? "s" : ""}`);
      setPayOpen(false); setAllocAmts({}); load(); loadStmtFiles(); loadRentStatus();
      return;
    }
    if (!payForm.description || !payForm.amount) return;
    const finalType = payForm.type === "Other" ? (payForm.customType.trim() || "Other") : payForm.type;
    await api.post("/transactions", {
      property_ref: p.reference,
      type: finalType,
      description: payForm.description,
      amount: parseFloat(payForm.amount),
      direction: payForm.direction,
      awaiting: payForm.awaiting,
      date: payForm.date || undefined,
      settle_txn_id: payForm.settle_txn_id || undefined,
    });
    if (payFile) {
      const fd = new FormData();
      fd.append("entity_type", "property");
      fd.append("entity_id", id);
      fd.append("bucket", "statement");
      fd.append("label", payForm.description);
      fd.append("file", payFile);
      try {
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch { /* non-blocking */ }
    }
    toast.success(payFile ? "Payment recorded & receipt attached to statement" : (payForm.settle_txn_id ? "Awaiting payment settled" : "Payment recorded"));
    setPayOpen(false);
    setPayForm({ type: "Rent", description: "", amount: "", direction: "in", date: "", awaiting: false, settle_txn_id: "", customType: "" });
    setPayFile(null);
    load();
    loadStmtFiles();
    loadRentStatus();
  };

  useEffect(() => {
    if (rentStatus) setRentForm({
      tenancy_start: rentStatus.tenancy_start ? rentStatus.tenancy_start.slice(0, 10) : "",
      rent_last_increased: rentStatus.rent_last_increased ? rentStatus.rent_last_increased.slice(0, 10) : "",
      tenant: rentStatus.tenant || "",
      tenant_email: rentStatus.tenant_email || "",
      tenant_phone: rentStatus.tenant_phone || "",
      rent_pcm: rentStatus.rent_pcm ? String(rentStatus.rent_pcm) : "",
    });
  }, [rentStatus]);

  useEffect(() => {
    if (rentStatus && (rentStatus.tenant || "").trim()) loadTenantInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentStatus?.tenant]);

  const setTenancyState = async (state) => {
    setRentStatus((rs) => rs ? { ...rs, tenancy_state: state } : rs);
    try {
      await api.patch(`/properties/${id}/rent-details`, { tenancy_state: state });
      toast.success(`Marked as ${state}`);
      await loadRentStatus();
      load();
    } catch { toast.error("Could not update state"); }
  };

  const openTenant = async () => {
    setTenantOpen(true);
    try { const { data } = await api.get(`/properties/${id}/tenant`); setTenantInfo(data); }
    catch { setTenantInfo(null); }
  };

  const saveRentDetails = async (e) => {
    e.preventDefault();
    setSavingRent(true);
    try {
      const { data } = await api.patch(`/properties/${id}/rent-details`, {
        tenancy_start: rentForm.tenancy_start || undefined,
        rent_last_increased: rentForm.rent_last_increased || undefined,
        tenant: rentForm.tenant || undefined,
        tenant_email: rentForm.tenant_email || undefined,
        tenant_phone: rentForm.tenant_phone || undefined,
        rent_pcm: rentForm.rent_pcm ? parseFloat(rentForm.rent_pcm) : undefined,
      });
      toast.success("Tenant & tenancy details saved");
      setIsEditingTenant(false);
      if (data?.tenant_login) setCreds({ ...data.tenant_login, role: "tenant" });
      await loadRentStatus();
      loadTenantInfo();
      load();
    } catch {
      toast.error("Could not save rent details");
    } finally {
      setSavingRent(false);
    }
  };

  const resetTenantLogin = async () => {
    if (!tenantInfo?.user_id) return;
    setResetBusy(true);
    try {
      const { data } = await api.post(`/users/${tenantInfo.user_id}/reset-password`);
      setCreds({ ...data, role: "tenant" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset login"); }
    finally { setResetBusy(false); }
  };

  const endTenancy = async () => {
    setTenancyBusy(true);
    try {
      await api.post(`/properties/${id}/end-tenancy`, { end_date: endDate || undefined });
      toast.success("Tenancy ended — moved to history");
      setEndOpen(false); setEndDate(""); setIsEditingTenant(false);
      await loadRentStatus();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not end tenancy"); }
    finally { setTenancyBusy(false); }
  };

  const addJoint = async (e) => {
    e.preventDefault();
    if (!jointForm.name.trim()) return;
    setTenancyBusy(true);
    try {
      await api.post(`/properties/${id}/tenants`, jointForm);
      toast.success("Joint tenant added");
      setJointOpen(false); setJointForm({ name: "", email: "", phone: "" });
      await loadRentStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add tenant"); }
    finally { setTenancyBusy(false); }
  };

  const removeJoint = async (index) => {
    await api.delete(`/properties/${id}/tenants/${index}`);
    await loadRentStatus();
  };

  const openHistory = async (index, entry) => {
    setHistEntry(entry); setHistDetail(null); setHistOpen(true);
    try { const { data } = await api.get(`/properties/${id}/tenancy-history/${index}`); setHistDetail(data); }
    catch { setHistDetail({ payments: [], documents: [], total_paid: 0 }); }
  };

  const onUploadStmt = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingStmt(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("entity_type", "property");
        fd.append("entity_id", id);
        fd.append("bucket", "statement");
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Attached — will be included in the PDF statement");
      await loadStmtFiles();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingStmt(false);
      if (stmtRef.current) stmtRef.current.value = "";
    }
  };

  const removeStmtFile = async (fid) => {
    await api.delete(`/files/${fid}`);
    toast.success("Attachment removed");
    await loadStmtFiles();
  };

  const addEvent = async (e) => {
    e.preventDefault();
    await api.post(`/properties/${id}/events`, evt);
    toast.success("Event recorded in Property DNA");
    setOpen(false);
    setEvt({ category: "Communication", title: "", description: "" });
    load();
  };

  if (!data) return <Layout title="Property"><Loading /></Layout>;
  const p = data.property;
  const availableBalance = (data.transactions || []).reduce((acc, t) => t.awaiting ? acc : acc + (t.direction === "in" ? t.amount : -t.amount), 0);
  const awaitingList = (data.transactions || []).filter((t) => t.awaiting);
  const allocTotal = Object.values(allocAmts).reduce((a, v) => a + (parseFloat(v) || 0), 0);

  const tabs = [
    { id: "dna", label: "Property DNA", icon: Dna },
    { id: "photos", label: "Photos", icon: ImageIcon },
    { id: "documents", label: "Documents", icon: FolderOpen },
    { id: "finance", label: "Finance", icon: Landmark },
    { id: "tasks", label: "Tasks", icon: ClipboardList },
    { id: "rent", label: "Rental Status", icon: PiggyBank },
  ];

  const heroSrc = p.cover_file_id ? fileUrl(p.cover_file_id) : p.image;

  return (
    <Layout>
      <button onClick={() => navigate("/properties")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-5 fade-up">
        <ArrowLeft size={16} /> Back to properties
      </button>

      <Card className="overflow-hidden mb-6 fade-up">
        <div className="h-56 relative">
          <img src={heroSrc} alt={p.address} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <Badge>{p.status}</Badge>
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight">{p.address}</h1>
            <p className="text-sm text-slate-200 flex items-center gap-1.5 mt-1"><MapPin size={14} /> {p.city}, {p.postcode}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-slate-100 border-t border-slate-100">
          <button data-testid="rent-status-card" onClick={() => setTab("rent")}
            className="p-4 text-left hover:bg-slate-50 transition-colors group">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
              Rental Status <ChevronRight size={12} className="text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" />
            </p>
            {rentStatus ? (() => {
              const meta = {
                up_to_date: { label: "Up to date", cls: "text-emerald-700 bg-emerald-50", Icon: CheckCircle2 },
                due_soon: { label: "Due soon", cls: "text-amber-700 bg-amber-50", Icon: CalendarClock },
                in_arrears: { label: `${gbp(rentStatus.owed)} owed`, cls: "text-red-700 bg-red-50", Icon: AlertTriangle },
                vacant: { label: "Vacant", cls: "text-slate-500 bg-slate-100", Icon: Home },
              }[rentStatus.status] || { label: "—", cls: "text-slate-500 bg-slate-100", Icon: Home };
              const { Icon } = meta;
              return (
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full ${meta.cls}`}>
                  <Icon size={13} /> {meta.label}
                </span>
              );
            })() : <span className="text-sm text-slate-300">—</span>}
          </button>
          <Stat label="Rent pcm" value={gbp(p.rent_pcm)} />
          <Stat label="Bedrooms" value={p.beds} icon={BedDouble} />
          <Stat label="Bathrooms" value={p.baths} icon={Bath} />
          <Stat label="Landlord" value={p.landlord || "—"} small />
          <button data-testid="tenant-card" onClick={openTenant}
            className="p-4 text-left hover:bg-slate-50 transition-colors group">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
              Tenant <ChevronRight size={12} className="text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" />
            </p>
            <p className="text-sm font-semibold text-slate-800 truncate">{p.tenant || "—"}</p>
          </button>
        </div>
      </Card>

      <div className="flex items-center gap-1 border-b border-slate-200 mb-6">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "dna" && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Dna size={18} className="text-blue-700" /> Property DNA</h3>
              <p className="text-xs text-slate-400 mt-0.5">A permanent, searchable history. Nothing is ever deleted.</p>
            </div>
            <button data-testid="add-event-button" onClick={() => setOpen(true)}
              className="bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-2 transition-colors">
              <Plus size={15} /> Record Event
            </button>
          </div>
          <div className="relative pl-8 dna-line">
            {data.events.map((e, i) => {
              const Icon = catIcon[e.category] || FileText;
              return (
                <div key={e.event_id} className="relative mb-6 fade-up" data-testid={`dna-event-${i}`}>
                  <div className={`absolute -left-8 top-0.5 w-6 h-6 rounded-full border-2 border-white shadow-sm flex items-center justify-center ${i === 0 ? "bg-blue-600 animate-pulse" : "bg-slate-700"}`}>
                    <Icon size={12} className="text-white" />
                  </div>
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge>{e.category}</Badge>
                      <span className="text-xs text-slate-400 font-mono ml-auto">{fmtDate(e.date)}</span>
                    </div>
                    <p className="font-medium text-slate-900 text-sm">{e.title}</p>
                    {e.description && <p className="text-sm text-slate-500 mt-0.5">{e.description}</p>}
                    <p className="text-xs text-slate-400 mt-2">by {e.actor}</p>
                  </Card>
                </div>
              );
            })}
            {data.events.length === 0 && <p className="text-sm text-slate-400">No events recorded yet.</p>}
          </div>
        </div>
      )}

      {tab === "photos" && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><ImageIcon size={18} className="text-blue-700" /> Listing Photos</h3>
              <p className="text-xs text-slate-400 mt-0.5">Upload images for the listing. The cover appears on the property card.</p>
            </div>
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={onUpload} className="hidden" data-testid="photo-file-input" />
            <div className="flex items-center gap-2">
              {photos.length > 0 && !selectPhotos && (
                <>
                  <button data-testid="download-all-photos-button" onClick={() => downloadPhotos(photos)} disabled={downloadingPhotos}
                    className="border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60">
                    {downloadingPhotos ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download all
                  </button>
                  <button data-testid="select-photos-button" onClick={() => { setSelectPhotos(true); setSelectedPhotos(new Set()); }}
                    className="border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-2 transition-colors">
                    <CheckCircle2 size={15} /> Select
                  </button>
                </>
              )}
              {photos.length > 0 && selectPhotos && (
                <>
                  <button data-testid="download-selected-photos-button" onClick={() => downloadPhotos(photos.filter((f) => selectedPhotos.has(f.file_id)))} disabled={downloadingPhotos || selectedPhotos.size === 0}
                    className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50">
                    {downloadingPhotos ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download selected ({selectedPhotos.size})
                  </button>
                  <button data-testid="select-all-photos-button" onClick={() => setSelectedPhotos(new Set(photos.map((f) => f.file_id)))}
                    className="border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-2 text-sm font-medium transition-colors">
                    Select all
                  </button>
                  <button data-testid="cancel-select-photos-button" onClick={() => { setSelectPhotos(false); setSelectedPhotos(new Set()); }}
                    className="border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-3 py-2 text-sm font-medium transition-colors">
                    Cancel
                  </button>
                </>
              )}
              <button data-testid="upload-photo-button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {uploading ? "Uploading…" : "Upload Images"}
              </button>
            </div>
          </div>

          {photos.length === 0 ? (
            <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-slate-200 rounded-xl py-16 flex flex-col items-center text-slate-400 hover:border-blue-400 hover:text-blue-600 transition-colors">
              <ImageIcon size={40} strokeWidth={1.2} />
              <p className="text-sm mt-3">No photos yet — click to upload listing images</p>
            </button>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((f, i) => {
                const isCover = p.cover_file_id === f.file_id;
                const isSelected = selectedPhotos.has(f.file_id);
                return (
                  <div key={f.file_id} data-testid={`photo-${f.file_id}`}
                    onClick={selectPhotos ? () => togglePhotoSelect(f.file_id) : undefined}
                    className={`group relative rounded-xl overflow-hidden border bg-slate-100 aspect-[4/3] fade-up transition-all ${selectPhotos ? "cursor-pointer" : ""} ${isSelected ? "border-blue-600 ring-2 ring-blue-500" : "border-slate-200"}`}>
                    {f.category === "video" ? (
                      <video src={fileUrl(f.file_id)} className="w-full h-full object-cover" />
                    ) : (
                      <img src={fileUrl(f.file_id)} alt={f.original_filename} className="w-full h-full object-cover" />
                    )}
                    <span className="absolute top-2 right-2 bg-slate-900/80 text-white text-[10px] font-semibold w-5 h-5 rounded-full flex items-center justify-center">{i + 1}</span>
                    {isCover && <span className="absolute top-2 left-2 bg-blue-700 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1"><Star size={10} /> Cover</span>}
                    {selectPhotos && (
                      <span data-testid={`photo-check-${f.file_id}`}
                        className={`absolute bottom-2 left-2 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors ${isSelected ? "bg-blue-600 border-blue-600 text-white" : "bg-white/80 border-white text-transparent"}`}>
                        <CheckCircle2 size={16} />
                      </span>
                    )}
                    {!selectPhotos && (
                      <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                        <button data-testid={`move-earlier-${f.file_id}`} onClick={() => movePhoto(i, -1)} disabled={i === 0} title="Move earlier"
                          className="w-8 h-8 rounded-lg bg-white text-slate-800 hover:bg-blue-50 flex items-center justify-center disabled:opacity-30"><ChevronLeft size={16} /></button>
                        <button data-testid={`move-later-${f.file_id}`} onClick={() => movePhoto(i, 1)} disabled={i === photos.length - 1} title="Move later"
                          className="w-8 h-8 rounded-lg bg-white text-slate-800 hover:bg-blue-50 flex items-center justify-center disabled:opacity-30"><ChevronRight size={16} /></button>
                        <button data-testid={`download-photo-${f.file_id}`} onClick={() => downloadPhotos([f])} title="Download"
                          className="w-8 h-8 rounded-lg bg-white text-slate-800 hover:bg-blue-50 flex items-center justify-center"><Download size={16} /></button>
                        {!isCover && (
                          <button data-testid={`set-cover-${f.file_id}`} onClick={() => setCover(f.file_id)} title="Set as cover"
                            className="w-8 h-8 rounded-lg bg-white text-slate-800 hover:bg-blue-50 flex items-center justify-center"><Star size={16} /></button>
                        )}
                        <button data-testid={`delete-photo-${f.file_id}`} onClick={() => removePhoto(f.file_id)} title="Remove"
                          className="w-8 h-8 rounded-lg bg-white text-red-600 hover:bg-red-50 flex items-center justify-center"><Trash2 size={16} /></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-4">Tip: use the ◀ ▶ controls to set which photo appears first, second, and so on. The numbered order is what visitors will see.</p>
        </div>
      )}

      {tab === "documents" && (
        <div>
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><FolderOpen size={18} className="text-blue-700" /> Documents & Certificates</h3>
              <p className="text-xs text-slate-400 mt-0.5">Upload files, images, videos, documents and certificates for this property.</p>
            </div>
            <div className="flex items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Type</label>
                <select data-testid="doc-label-select" value={docLabel} onChange={(e) => setDocLabel(e.target.value)}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                  {["General Document", "Gas Safety Certificate", "EPC Certificate", "Electrical Certificate (EICR)", "Tenancy Agreement", "Inventory", "Insurance", "Floor Plan", "Other Certificate"].map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Expiry date {docNeedsExpiry ? <span className="text-red-500">*required</span> : <span className="text-slate-400">(certificates)</span>}</label>
                <input data-testid="doc-expiry-input" type="date" value={docExpiry} onChange={(e) => setDocExpiry(e.target.value)}
                  className={`rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 ${docNeedsExpiry && !docExpiry ? "border-red-300 bg-red-50" : "border-slate-300"}`} />
              </div>
              <input ref={docRef} type="file" multiple onChange={onUploadDoc} className="hidden" data-testid="doc-file-input" />
              <button data-testid="upload-doc-button" onClick={() => { if (docNeedsExpiry && !docExpiry) { toast.error("Please enter the expiry date before uploading this certificate"); return; } docRef.current?.click(); }} disabled={uploadingDoc || (docNeedsExpiry && !docExpiry)}
                className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                {uploadingDoc ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {uploadingDoc ? "Uploading…" : "Upload File"}
              </button>
            </div>
          </div>

          {docs.length === 0 ? (
            <button onClick={() => { if (docNeedsExpiry && !docExpiry) { toast.error("Please enter the expiry date before uploading this certificate"); return; } docRef.current?.click(); }} className="w-full border-2 border-dashed border-slate-200 rounded-xl py-16 flex flex-col items-center text-slate-400 hover:border-blue-400 hover:text-blue-600 transition-colors">
              <FolderOpen size={40} strokeWidth={1.2} />
              <p className="text-sm mt-3">No documents yet — upload certificates, agreements and files</p>
            </button>
          ) : (
            <div className="space-y-2">
              {docs.map((f) => {
                const isImg = f.category === "photo";
                const isVid = f.category === "video";
                return (
                  <Card key={f.file_id} data-testid={`doc-${f.file_id}`} className="p-3 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center overflow-hidden shrink-0">
                      {isImg ? <img src={fileUrl(f.file_id)} alt="" className="w-full h-full object-cover" /> : <FileText size={20} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-slate-800 truncate">{f.label || f.original_filename}</p>
                        <Badge>{f.label ? f.label.split(" ")[0] : (isVid ? "Video" : isImg ? "Image" : "Doc")}</Badge>
                        {(() => {
                          if (!f.expiry_date) return null;
                          const days = Math.ceil((new Date(f.expiry_date) - new Date()) / 86400000);
                          const cls = days < 0 ? "bg-red-100 text-red-700" : days <= 30 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
                          const txt = days < 0 ? "Expired" : `Expires ${fmtDate(f.expiry_date)}`;
                          return <span data-testid={`doc-expiry-badge-${f.file_id}`} className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{txt}</span>;
                        })()}
                      </div>
                      <p className="text-xs text-slate-400 truncate">{f.original_filename} · {(f.size / 1024).toFixed(0)} KB · {fmtDate(f.date)} · {f.uploader_name}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-[11px] text-slate-400">Expiry:</span>
                        <input data-testid={`doc-expiry-edit-${f.file_id}`} type="date" value={f.expiry_date ? f.expiry_date.slice(0, 10) : ""}
                          onChange={(e) => updateDocExpiry(f.file_id, e.target.value)}
                          className="text-xs rounded border border-slate-200 px-1.5 py-0.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      </div>
                    </div>
                    <a data-testid={`download-doc-${f.file_id}`} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer"
                      className="w-9 h-9 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center justify-center" title="Download / view"><Download size={16} /></a>
                    <button data-testid={`delete-doc-${f.file_id}`} onClick={() => removeDoc(f.file_id)}
                      className="w-9 h-9 rounded-lg border border-slate-300 text-red-600 hover:bg-red-50 flex items-center justify-center" title="Remove"><Trash2 size={16} /></button>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "finance" && (
        <div>
          <OpenBankingCard propertyId={id} onRecorded={() => { load(); loadRentStatus(); loadStmtFiles(); }} />
          {rentStatus && (() => {
            const s = rentStatus;
            const meta = {
              up_to_date: { cls: "bg-emerald-50 border-emerald-200", badge: "text-emerald-700 bg-emerald-100", label: "Up to date", Icon: CheckCircle2 },
              due_soon: { cls: "bg-amber-50 border-amber-200", badge: "text-amber-700 bg-amber-100", label: `${gbp(s.owed)} due`, Icon: CalendarClock },
              in_arrears: { cls: "bg-red-50 border-red-200", badge: "text-red-700 bg-red-100", label: `${gbp(s.owed)} in arrears`, Icon: AlertTriangle },
              vacant: { cls: "bg-slate-50 border-slate-200", badge: "text-slate-600 bg-slate-100", label: "Vacant", Icon: Home },
            }[s.status];
            const M = meta.Icon;
            const pct = s.expected > 0 ? Math.min(100, Math.round((s.paid / s.expected) * 100)) : 0;
            const items = [
              { label: "Rent pcm", value: gbp(s.rent_pcm) },
              { label: "Rental start", value: s.tenancy_start ? fmtDate(s.tenancy_start) : "—" },
              { label: "Last increased", value: s.rent_last_increased ? fmtDate(s.rent_last_increased) : "None" },
              { label: "Last payment", value: s.last_payment_date ? `${gbp(s.last_payment_amount)} · ${fmtDate(s.last_payment_date)}` : "None" },
              { label: "Next rent due", value: s.next_due_date ? fmtDate(s.next_due_date) : "—" },
              { label: "Paid to date", value: `${gbp(s.paid)} / ${gbp(s.expected)}` },
            ];
            return (
              <Card data-testid="rental-summary-bar" className={`p-4 mb-4 border ${meta.cls} fade-up`}>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full ${meta.badge}`}>
                    <M size={15} /> {meta.label}
                  </span>
                  <p className="text-sm text-slate-600 font-medium">{s.tenant ? `Tenant: ${s.tenant}` : "No active tenant"}</p>
                  <button data-testid="rental-bar-edit" onClick={() => setTab("rent")} className="ml-auto text-sm text-blue-700 font-medium hover:text-blue-800 flex items-center gap-1">
                    Full rent status <ChevronRight size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-3 mb-3">
                  {items.map((it) => (
                    <div key={it.label}>
                      <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-0.5">{it.label}</p>
                      <p className="text-sm font-semibold text-slate-800">{it.value}</p>
                    </div>
                  ))}
                </div>
                {s.occupied && (
                  <div>
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span>Rent collected this tenancy</span>
                      <span className="tabular-nums font-medium">{pct}% ({s.months_paid} of {s.months_due} months)</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                      <div className={`h-full rounded-full ${s.owed > 0 ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}
              </Card>
            );
          })()}
          <div className="flex flex-wrap items-end gap-3 mb-4 fade-up">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
              <input data-testid="statement-from" type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
              <input data-testid="statement-to" type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            {(range.from || range.to) && (
              <button onClick={() => setRange({ from: "", to: "" })} className="text-sm text-slate-500 hover:text-slate-800 px-2 py-2">Clear</button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button data-testid="add-awaiting-payment-button" onClick={() => openPay(true)}
                className="bg-white text-slate-600 border border-slate-300 hover:bg-slate-50 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors">
                <Plus size={15} /> Add awaiting payment
              </button>
              <button data-testid="add-payment-button" onClick={() => openPay(false)}
                className="bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors">
                <Plus size={15} /> Add Payment
              </button>
              <div className="relative">
                <button data-testid="download-statement-button" onClick={() => setDlOpen((o) => !o)}
                  className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors">
                  <Download size={15} /> Download Statement <ChevronDown size={14} />
                </button>
                {dlOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setDlOpen(false)} />
                    <div className="absolute right-0 mt-2 w-44 bg-white border border-slate-200 rounded-xl shadow-xl z-20 overflow-hidden py-1">
                      <button data-testid="download-pdf" onClick={() => downloadStatement("pdf")} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                        <FileText size={15} className="text-red-500" /> PDF document
                      </button>
                      <button data-testid="download-excel" onClick={() => downloadStatement("xlsx")} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                        <Sheet size={15} className="text-emerald-600" /> Excel spreadsheet
                      </button>
                      <button data-testid="download-csv" onClick={() => downloadStatement("csv")} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                        <FileText size={15} className="text-slate-400" /> CSV file
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <Card className="overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-500 uppercase text-xs font-semibold tracking-wider">
                <tr><th className="py-3 px-4 text-left">Date</th><th className="text-left">Type</th><th className="text-left">Description</th><th className="text-right px-4">Amount</th><th className="w-10"></th></tr>
              </thead>
              <tbody>
                {data.transactions.filter((t) => inRange(t.date)).map((t) => (
                  <tr key={t.txn_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 group">
                    <td className="py-3 px-4 text-sm text-slate-500 font-mono">{fmtDate(t.date)}</td>
                    <td className="text-sm"><Badge>{t.type}</Badge>{t.awaiting && <span className="ml-1.5 text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">AWAITING</span>}</td>
                    <td className="text-sm text-slate-700">{t.description}</td>
                    <td className={`text-sm px-4 text-right tabular-nums font-medium ${t.awaiting ? "text-slate-400" : t.direction === "in" ? "text-emerald-600" : "text-red-500"}`}>
                      {gbp(t.amount)}
                    </td>
                    <td className="pr-3 text-right">
                      <button data-testid={`delete-txn-${t.txn_id}`} onClick={() => deleteTxn(t)} title="Delete payment"
                        className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
                {data.transactions.filter((t) => inRange(t.date)).length === 0 && <tr><td colSpan="5" className="text-center text-sm text-slate-400 py-8">No transactions in this period.</td></tr>}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {tab === "tasks" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-slate-900">Tasks for this property</h3>
            <button data-testid="add-property-task-button" onClick={() => setTaskOpen(true)}
              className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors">
              <Plus size={15} /> Add Task
            </button>
          </div>
          <div className="space-y-2">
            {data.tasks.map((t) => {
              const atts = taskAttachments[t.task_id] || [];
              return (
                <Card key={t.task_id} className="p-4" data-testid={`ptask-${t.task_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex-1"><p className="text-sm font-medium text-slate-800">{t.title}</p><p className="text-xs text-slate-400">{t.assignee} · Due {fmtDate(t.due_date)}</p></div>
                    {atts.length > 0 && <span className="text-xs text-slate-400 flex items-center gap-1"><Paperclip size={13} /> {atts.length}</span>}
                    <Badge>{t.priority}</Badge><Badge>{t.status}</Badge>
                  </div>
                  {atts.length > 0 && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 flex-wrap">
                      {atts.map((f) => (
                        <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" data-testid={`task-att-${f.file_id}`}
                          className="w-14 h-14 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center hover:ring-2 hover:ring-blue-500 transition-all">
                          {f.category === "photo" ? <img src={fileUrl(f.file_id)} alt="" className="w-full h-full object-cover" />
                            : f.category === "video" ? <video src={fileUrl(f.file_id)} className="w-full h-full object-cover" />
                            : <FileText size={18} className="text-slate-400" />}
                        </a>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
            {data.tasks.length === 0 && <p className="text-sm text-slate-400">No tasks linked to this property yet.</p>}
          </div>
        </div>
      )}

      {tab === "rent" && (
        <div data-testid="rent-status-tab">
          <div className="mb-5">
            <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><PiggyBank size={18} className="text-blue-700" /> Rental Status</h3>
            <p className="text-xs text-slate-400 mt-0.5">{rentStatus?.tenant ? `Tenant: ${rentStatus.tenant}` : "No active tenant"}</p>
          </div>

          <div className="mb-5" data-testid="tenancy-state-selector">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">Tenancy state</p>
            <div className="flex flex-wrap gap-2">
              {["Tenanted", "Vacant", "On the market for rent"].map((st) => {
                const active = (rentStatus?.tenancy_state || "") === st;
                const color = st === "Tenanted" ? "bg-emerald-600 border-emerald-600" : st === "Vacant" ? "bg-slate-600 border-slate-600" : "bg-amber-600 border-amber-600";
                return (
                  <button key={st} data-testid={`state-${st.replace(/ /g, "-")}`} onClick={() => setTenancyState(st)}
                    className={`text-sm font-medium px-3.5 py-1.5 rounded-full border transition-colors ${active ? `${color} text-white` : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                    {st}
                  </button>
                );
              })}
            </div>
          </div>

          {!rentStatus ? <Loading /> : (() => {
            const s = rentStatus;
            const banner = {
              up_to_date: { cls: "bg-emerald-50 border-emerald-200 text-emerald-800", Icon: CheckCircle2, title: "Up to date", note: "This tenancy is fully paid up." },
              due_soon: { cls: "bg-amber-50 border-amber-200 text-amber-800", Icon: CalendarClock, title: `${gbp(s.owed)} outstanding`, note: "Less than one month behind." },
              in_arrears: { cls: "bg-red-50 border-red-200 text-red-800", Icon: AlertTriangle, title: `${gbp(s.owed)} in arrears`, note: "Rent is behind schedule." },
              vacant: { cls: "bg-slate-50 border-slate-200 text-slate-600", Icon: Home, title: "Vacant", note: "No tenancy currently active." },
            }[s.status];
            const B = banner.Icon;
            const rows = [
              { label: "Rent per month", value: gbp(s.rent_pcm), Icon: PiggyBank },
              { label: "Rental start date", value: s.tenancy_start ? fmtDate(s.tenancy_start) : "Not recorded", Icon: CalendarClock },
              { label: "Next rent due", value: s.next_due_date ? fmtDate(s.next_due_date) : "—", Icon: CalendarClock },
              { label: "Rent last increased", value: s.rent_last_increased ? fmtDate(s.rent_last_increased) : "No increases recorded", Icon: TrendingUp },
              { label: "Paid to date", value: `${gbp(s.paid)} of ${gbp(s.expected)} due`, Icon: TrendingUp },
              { label: "Payments recorded", value: `${(s.payments || []).length}`, Icon: CheckCircle2 },
            ];
            return (
              <>
                <div className={`rounded-xl border p-4 flex items-center gap-3 mb-5 ${banner.cls}`} data-testid="rent-status-banner">
                  <B size={26} />
                  <div>
                    <p className="font-display text-lg font-bold leading-tight">{banner.title}</p>
                    <p className="text-xs opacity-80">{banner.note}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                  {rows.map((r) => (
                    <div key={r.label} className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1"><r.Icon size={12} /> {r.label}</p>
                      <p className="text-sm font-semibold text-slate-800">{r.value}</p>
                    </div>
                  ))}
                </div>

                <Card className="overflow-hidden mb-6" data-testid="rent-payments-list">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <h4 className="font-semibold text-slate-800 text-sm">Recent rent payments</h4>
                    <span className="text-xs text-slate-400">{(s.payments || []).length} payment{(s.payments || []).length === 1 ? "" : "s"}</span>
                  </div>
                  {(s.payments || []).length === 0 ? (
                    <p className="text-sm text-slate-400 py-8 text-center">No rent payments recorded yet.</p>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-xs font-semibold tracking-wider">
                        <tr><th className="py-2.5 px-4 text-left">Date</th><th className="text-left">Description</th><th className="text-right px-4">Amount</th></tr>
                      </thead>
                      <tbody>
                        {(s.payments || []).map((pm, i) => (
                          <tr key={i} data-testid={`rent-payment-${i}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                            <td className="py-2.5 px-4 text-sm text-slate-600 font-mono">{fmtDate(pm.date)}</td>
                            <td className="text-sm text-slate-700">{pm.description || "Rent received"}</td>
                            <td className="text-sm px-4 text-right tabular-nums font-medium text-emerald-600">{gbp(pm.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card>

                {(() => {
                  const hasTenant = !!(rentStatus?.tenant || "").trim();
                  const locked = hasTenant && !isEditingTenant;
                  const joints = rentStatus?.additional_tenants || [];
                  const inputCls = `w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 ${locked ? "border-slate-200 bg-slate-50 text-slate-500" : "border-slate-300"}`;
                  return (
                <div className="border-t border-slate-100 pt-5 space-y-4 max-w-2xl" data-testid="tenancy-manage-section">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-700">{hasTenant ? "Tenant & tenancy details" : "Add tenant & tenancy details"}</p>
                    {locked && (
                      <button type="button" data-testid="edit-tenant-button" onClick={() => setIsEditingTenant(true)}
                        className="text-sm text-blue-700 hover:text-blue-800 border border-blue-200 rounded-lg px-3 py-1.5 font-medium flex items-center gap-1.5"><Pencil size={14} /> Edit details</button>
                    )}
                  </div>
                  <form onSubmit={saveRentDetails} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Tenant name</label>
                      <input data-testid="rent-tenant-name" disabled={locked} value={rentForm.tenant} onChange={(e) => setRentForm({ ...rentForm, tenant: e.target.value })} placeholder="e.g. Daisy Cohen" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Rent per month (£)</label>
                      <input data-testid="rent-pcm" disabled={locked} type="number" min="0" step="1" value={rentForm.rent_pcm} onChange={(e) => setRentForm({ ...rentForm, rent_pcm: e.target.value })} placeholder="e.g. 950" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Tenant email</label>
                      <input data-testid="rent-tenant-email" disabled={locked} type="email" value={rentForm.tenant_email} onChange={(e) => setRentForm({ ...rentForm, tenant_email: e.target.value })} placeholder="tenant@email.com" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Tenant phone</label>
                      <input data-testid="rent-tenant-phone" disabled={locked} value={rentForm.tenant_phone} onChange={(e) => setRentForm({ ...rentForm, tenant_phone: e.target.value })} placeholder="07…" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Rental start / moving-in date <span className="text-slate-400">(sets rent due date)</span></label>
                      <input data-testid="rent-tenancy-start" disabled={locked} type="date" value={rentForm.tenancy_start} onChange={(e) => setRentForm({ ...rentForm, tenancy_start: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Rent last increased</label>
                      <input data-testid="rent-last-increased" disabled={locked} type="date" value={rentForm.rent_last_increased} onChange={(e) => setRentForm({ ...rentForm, rent_last_increased: e.target.value })} className={inputCls} />
                    </div>
                  </div>
                  {!locked && (
                    <div className="flex items-center gap-2">
                      <button data-testid="save-rent-details-button" type="submit" disabled={savingRent}
                        className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-5 py-2.5 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                        {savingRent ? <Loader2 size={16} className="animate-spin" /> : null} Save Details
                      </button>
                      {isEditingTenant && (
                        <button type="button" data-testid="cancel-edit-tenant" onClick={() => { setIsEditingTenant(false); loadRentStatus(); }}
                          className="text-sm text-slate-500 hover:text-slate-700 px-3 py-2.5">Cancel</button>
                      )}
                    </div>
                  )}
                  </form>

                  {hasTenant && (
                    <div className="rounded-xl border border-slate-200 p-4" data-testid="tenant-portal-login">
                      <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><KeyRound size={15} className="text-blue-700" /> Tenant portal login</p>
                      {tenantInfo?.has_account ? (
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                          <span className="text-sm text-slate-600">{tenantInfo.email}</span>
                          <button type="button" data-testid="reset-tenant-login" onClick={resetTenantLogin} disabled={resetBusy}
                            className="ml-auto text-sm border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-1.5 font-medium flex items-center gap-1.5 disabled:opacity-60">
                            {resetBusy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Reset &amp; show password
                          </button>
                        </div>
                      ) : (rentForm.tenant_email || rentStatus?.tenant_email) ? (
                        <p className="text-sm text-slate-500">Saving the tenant details above automatically creates their portal login and emails it to them.</p>
                      ) : (
                        <p className="text-sm text-slate-500">Add a tenant email above to create their portal login.</p>
                      )}
                    </div>
                  )}

                  {hasTenant && (
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <button type="button" data-testid="add-joint-tenant-button" onClick={() => setJointOpen(true)}
                        className="text-sm text-slate-700 hover:bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 font-medium flex items-center gap-1.5"><UserPlus size={15} /> Add tenant to this tenancy</button>
                      <button type="button" data-testid="finish-tenancy-button" onClick={() => setEndOpen(true)}
                        className="text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded-lg px-3 py-2 font-medium flex items-center gap-1.5"><History size={15} /> Finish tenancy</button>
                    </div>
                  )}

                  {joints.length > 0 && (
                    <div data-testid="joint-tenants-list" className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                      <p className="text-xs font-medium text-slate-500 px-3 py-2 bg-slate-50">Joint tenants on this tenancy</p>
                      {joints.map((jt, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-2" data-testid={`joint-tenant-${i}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{jt.name}</p>
                            <p className="text-xs text-slate-400 truncate">{[jt.email, jt.phone].filter(Boolean).join(" · ") || "No contact details"}</p>
                          </div>
                          <button type="button" data-testid={`remove-joint-${i}`} onClick={() => removeJoint(i)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}

                  {(rentStatus?.tenancy_history || []).length > 0 && (
                    <div data-testid="tenancy-history" className="pt-6 mt-4 border-t border-slate-100">
                      <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3"><History size={15} /> Tenancy history</p>
                      <div className="space-y-2">
                        {rentStatus.tenancy_history.map((h, i) => (
                          <button key={i} type="button" data-testid={`history-entry-${i}`} onClick={() => openHistory(i, h)}
                            className="w-full text-left rounded-lg border border-slate-200 bg-slate-50 hover:bg-white hover:border-blue-300 hover:shadow-sm transition-all px-4 py-3">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-semibold text-slate-800">{h.tenant}{(h.additional_tenants || []).length > 0 && <span className="text-slate-500 font-normal"> + {h.additional_tenants.map((t) => t.name).join(", ")}</span>}</p>
                              <span className="text-xs text-slate-500 flex items-center gap-1.5">{gbp(h.rent_pcm)}/mo <ChevronRight size={13} className="text-slate-400" /></span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {h.tenancy_start ? fmtDate(h.tenancy_start) : "—"} → {h.tenancy_end ? fmtDate(h.tenancy_end) : "—"}
                              {[h.tenant_email, h.tenant_phone].filter(Boolean).length > 0 && <> · {[h.tenant_email, h.tenant_phone].filter(Boolean).join(" · ")}</>}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {refHistory.length > 0 && (
                    <div data-testid="referencing-history" className="pt-6 mt-4 border-t border-slate-100">
                      <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3"><ClipboardList size={15} /> Referencing history</p>
                      <div className="space-y-2">
                        {refHistory.map((c) => (
                          <button key={c.case_id} type="button" data-testid={`ref-history-${c.reference}`}
                            onClick={() => navigate(`/lettings?ref=${c.case_id}`)}
                            className="w-full text-left rounded-lg border border-slate-200 bg-slate-50 hover:bg-white hover:border-blue-300 hover:shadow-sm transition-all px-4 py-3">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-semibold text-slate-800">
                                <span className="font-mono text-xs text-blue-700 mr-2">{c.reference}</span>
                                {(c.applicants || []).map((a) => a.name).filter(Boolean).join(", ") || "No applicants"}
                              </p>
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${c.status === "Tenanted" ? "bg-emerald-100 text-emerald-700" : c.status === "Complete" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>{c.status || "In Progress"}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {c.created_at ? fmtDate(c.created_at) : "—"} · {gbp(c.rent_pcm)}/mo
                              {(c.applicants || []).some((a) => a.rent_status === "paid") && <span className="text-emerald-600"> · Tenancy live</span>}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                  );
                })()}
              </>
            );
          })()}
        </div>
      )}


      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Record DNA Event</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={addEvent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <select value={evt.category} onChange={(e) => setEvt({ ...evt, category: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                  {Object.keys(catIcon).map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input data-testid="event-title-input" required value={evt.title} onChange={(e) => setEvt({ ...evt, title: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" placeholder="e.g. Boiler serviced" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea value={evt.description} onChange={(e) => setEvt({ ...evt, description: e.target.value })} rows={3} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <button data-testid="submit-event-button" type="submit" className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium transition-colors">Record Event</button>
            </form>
          </div>
        </div>
      )}

      {taskOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setTaskOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Add Task — {p.address}</h3>
              <button onClick={() => setTaskOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={addTask} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input data-testid="property-task-title-input" required value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" placeholder="e.g. Book EPC assessment" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Assignee</label>
                  <input value={taskForm.assignee} onChange={(e) => setTaskForm({ ...taskForm, assignee: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <select value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    {["Low", "Medium", "High", "Urgent"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                <input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Photos & Videos</label>
                <input ref={taskFileRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid="task-file-input"
                  onChange={(e) => setTaskFiles(Array.from(e.target.files || []))} />
                <button type="button" data-testid="task-attach-button" onClick={() => taskFileRef.current?.click()}
                  className="w-full border-2 border-dashed border-slate-200 rounded-lg py-3 flex items-center justify-center gap-2 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors">
                  <Paperclip size={15} /> {taskFiles.length ? `${taskFiles.length} file(s) selected` : "Attach photos or videos"}
                </button>
                {taskFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {taskFiles.map((f, i) => (
                      <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md flex items-center gap-1">
                        {f.type.startsWith("video") ? "🎬" : "🖼️"} {f.name.slice(0, 18)}
                        <button type="button" onClick={() => setTaskFiles(taskFiles.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button data-testid="submit-property-task-button" type="submit" disabled={savingTask} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {savingTask ? <Loader2 size={16} className="animate-spin" /> : null} Add Task
              </button>
            </form>
          </div>
        </div>
      )}

      {payOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setPayOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">{payForm.awaiting ? "Add Awaiting Payment" : "Add Payment"} — {p.address}</h3>
              <button onClick={() => setPayOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            {payForm.awaiting && payForm.type !== "Allocate Payment" && <p className="text-xs text-slate-500 bg-slate-100 rounded-lg px-3 py-2 mb-4">This is a payment the landlord owes but funds are not yet available. It shows separately (in grey) on the statement and is not deducted from the balance.</p>}
            {(!payForm.awaiting || payForm.type === "Allocate Payment") && (
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4" data-testid="available-balance">
                <span className="text-xs font-medium text-slate-500">Available balance</span>
                <span className={`text-sm font-bold tabular-nums ${availableBalance < 0 ? "text-red-600" : "text-emerald-600"}`}>{gbp(availableBalance)}</span>
              </div>
            )}
            <form onSubmit={addPayment} className="space-y-4">
              <div className={payForm.type === "Allocate Payment" ? "" : "grid grid-cols-2 gap-4"}>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select data-testid="payment-type" value={payForm.type} onChange={(e) => onTypeChange(e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    <option value="Allocate Payment">Allocate Payment</option>
                    {(payForm.awaiting
                      ? ["Maintenance", "Council Tax", "Insurance", "Licence", "Admin Fee", "Other"]
                      : ["Maintenance", "Council Tax", "Insurance", "Licence", "Admin Fee", "Rent", "Landlord Payment", "Deposit", "Other"]
                    ).map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                {payForm.type !== "Allocate Payment" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Direction</label>
                  <select data-testid="payment-direction" value={payForm.direction} onChange={(e) => setPayForm({ ...payForm, direction: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    <option value="in">Money In</option>
                    <option value="out">Money Out</option>
                  </select>
                </div>
                )}
              </div>

              {payForm.type === "Allocate Payment" ? (
                <div data-testid="allocate-panel">
                  <p className="text-xs text-slate-500 mb-2">Tick a payment to clear it in full, or enter a part amount. Total can't exceed {gbp(availableBalance)}.</p>
                  {awaitingList.length === 0 ? (
                    <p className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg">No awaiting payments to allocate.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-60 overflow-y-auto">
                      {awaitingList.map((t) => {
                        const checked = t.txn_id in allocAmts;
                        const remaining = Math.max(0, availableBalance - (allocTotal - (parseFloat(allocAmts[t.txn_id]) || 0)));
                        const toggle = (on) => setAllocAmts((s) => {
                          const n = { ...s };
                          if (on) n[t.txn_id] = String(Math.min(t.amount, remaining).toFixed(2));
                          else delete n[t.txn_id];
                          return n;
                        });
                        const over = checked && (parseFloat(allocAmts[t.txn_id]) || 0) > t.amount + 0.001;
                        return (
                          <div key={t.txn_id} data-testid={`alloc-item-${t.txn_id}`}
                            className={`px-3 py-2 rounded-lg border ${checked ? "border-blue-300 bg-blue-50" : "border-slate-200"}`}>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={checked} onChange={(e) => toggle(e.target.checked)} className="w-4 h-4 accent-blue-700" />
                              <span className="flex-1 min-w-0"><span className="text-sm text-slate-800 block truncate">{t.type} — {t.description}</span></span>
                              <span className="text-sm font-medium text-slate-700 tabular-nums">{gbp(t.amount)}</span>
                            </label>
                            {checked && (
                              <div className="flex items-center gap-2 mt-2 pl-6">
                                <span className="text-xs text-slate-500">Allocate £</span>
                                <input data-testid={`alloc-amount-${t.txn_id}`} type="number" step="0.01" min="0" max={t.amount}
                                  value={allocAmts[t.txn_id]} onChange={(e) => setAllocAmts((s) => ({ ...s, [t.txn_id]: e.target.value }))}
                                  className={`w-28 rounded-md border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 ${over ? "border-red-400" : "border-slate-300"}`} />
                                {(parseFloat(allocAmts[t.txn_id]) || 0) < t.amount - 0.001 && !over && <span className="text-[11px] text-amber-600">part — {gbp(t.amount - (parseFloat(allocAmts[t.txn_id]) || 0))} left awaiting</span>}
                                {over && <span className="text-[11px] text-red-600">exceeds amount owed</span>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <span className="text-sm font-medium text-slate-600">Total to allocate</span>
                    <span data-testid="alloc-total" className={`text-sm font-bold tabular-nums ${allocTotal > availableBalance + 0.001 ? "text-red-600" : "text-slate-900"}`}>{gbp(allocTotal)}</span>
                  </div>
                </div>
              ) : (
                <>
                  {payForm.type === "Other" && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Custom wording</label>
                      <input data-testid="payment-custom-type" value={payForm.customType} onChange={(e) => setPayForm({ ...payForm, customType: e.target.value })} placeholder="e.g. Ground rent, Service charge…" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    </div>
                  )}
                  {payForm.type === "Maintenance" && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">A maintenance fee will be added automatically based on this property's maintenance fee %.</p>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                    <input data-testid="payment-description" required value={payForm.description} onChange={(e) => setPayForm({ ...payForm, description: e.target.value })} placeholder="e.g. Rent received - March" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Amount (£)</label>
                      <input data-testid="payment-amount" required type="number" step="0.01" min="0" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} placeholder="1650.00" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                      <input data-testid="payment-date" type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    </div>
                  </div>
                </>
              )}

              {payForm.type !== "Allocate Payment" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Receipt / Invoice (PDF or photo)</label>
                <input ref={stmtRef} type="file" accept="application/pdf,image/*" className="hidden" data-testid="payment-file-input"
                  onChange={(e) => setPayFile(e.target.files?.[0] || null)} />
                <button type="button" data-testid="payment-attach-button" onClick={() => stmtRef.current?.click()}
                  className="w-full border-2 border-dashed border-slate-200 rounded-lg py-3 flex items-center justify-center gap-2 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors">
                  <Paperclip size={15} /> {payFile ? payFile.name.slice(0, 30) : "Attach a receipt (auto-added to PDF statement)"}
                </button>
                {payFile && (
                  <button type="button" onClick={() => { setPayFile(null); if (stmtRef.current) stmtRef.current.value = ""; }} className="text-xs text-slate-400 hover:text-red-500 mt-1.5 flex items-center gap-1"><X size={12} /> Remove attachment</button>
                )}
                {stmtFiles.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-slate-400 mb-1.5">Already attached to this statement ({stmtFiles.length}):</p>
                    <div className="flex flex-wrap gap-2">
                      {stmtFiles.map((f) => (
                        <span key={f.file_id} data-testid={`stmt-att-${f.file_id}`} className="text-xs bg-slate-100 border border-slate-200 rounded-lg px-2.5 py-1 flex items-center gap-1.5">
                          {f.category === "photo" ? <ImageIcon size={12} className="text-blue-600" /> : <FileText size={12} className="text-red-500" />}
                          <span className="max-w-[130px] truncate">{f.original_filename}</span>
                          <button type="button" data-testid={`stmt-att-del-${f.file_id}`} onClick={() => removeStmtFile(f.file_id)} className="text-slate-400 hover:text-red-500"><X size={11} /></button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              )}
              <button data-testid="submit-payment-button" type="submit"
                disabled={payForm.type === "Allocate Payment" && (allocTotal <= 0 || allocTotal > availableBalance + 0.001)}
                className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium transition-colors disabled:opacity-50">
                {payForm.type === "Allocate Payment" ? `Allocate ${allocTotal > 0 ? gbp(allocTotal) : "Payment"}` : (payForm.awaiting ? "Add Awaiting Payment" : "Record Payment")}
              </button>
            </form>
          </div>
        </div>
      )}
      <CredentialsDialog creds={creds} title="Tenant portal login" onClose={() => setCreds(null)} />

      {tenantOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setTenantOpen(false)}>
          <div data-testid="tenant-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><Users size={18} className="text-blue-700" /> Tenant Details</h3>
              <button onClick={() => setTenantOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            {!tenantInfo ? <Loading /> : !tenantInfo.occupied ? (
              <p className="text-sm text-slate-500 py-6 text-center">This property has no active tenant ({tenantInfo.tenancy_state || "Vacant"}).</p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-lg">{(tenantInfo.name || "T")[0]}</div>
                  <div>
                    <p className="font-semibold text-slate-900">{tenantInfo.name}</p>
                    <p className="text-xs text-slate-400">{tenantInfo.has_account ? "Has tenant portal account" : "No portal account"}</p>
                  </div>
                </div>
                {[
                  { label: "Email", value: tenantInfo.email || "Not recorded" },
                  { label: "Phone", value: tenantInfo.phone || "Not recorded" },
                  { label: "Rent per month", value: gbp(tenantInfo.rent_pcm) },
                  { label: "Rental start", value: tenantInfo.tenancy_start ? fmtDate(tenantInfo.tenancy_start) : "Not recorded" },
                  { label: "Tenancy state", value: tenantInfo.tenancy_state || "—" },
                ].map((r) => (
                  <div key={r.label} className="flex justify-between text-sm border-b border-slate-100 pb-2">
                    <span className="text-slate-400">{r.label}</span>
                    <span className="font-medium text-slate-800">{r.value}</span>
                  </div>
                ))}
                <button onClick={() => { setTenantOpen(false); setTab("rent"); }} className="w-full mt-2 text-sm text-blue-700 font-medium hover:text-blue-800">Edit in Rental Status →</button>
              </div>
            )}
          </div>
        </div>
      )}

      {endOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setEndOpen(false)}>
          <div data-testid="end-tenancy-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold text-slate-900">Finish tenancy</h3>
              <button type="button" onClick={() => setEndOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">This moves <b>{rentStatus?.tenant || "the current tenant"}</b>{(rentStatus?.additional_tenants || []).length > 0 ? " and joint tenants" : ""} to tenancy history and marks the property as vacant so you can add a new tenant.</p>
            <label className="block text-xs font-medium text-slate-500 mb-1">Tenancy end date</label>
            <input data-testid="end-tenancy-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <div className="flex gap-2">
              <button data-testid="confirm-end-tenancy" onClick={endTenancy} disabled={tenancyBusy}
                className="flex-1 bg-red-600 text-white hover:bg-red-700 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {tenancyBusy ? <Loader2 size={16} className="animate-spin" /> : null} End tenancy
              </button>
              <button type="button" onClick={() => setEndOpen(false)} className="px-4 py-2.5 text-sm text-slate-500 hover:text-slate-700">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {jointOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setJointOpen(false)}>
          <div data-testid="joint-tenant-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold text-slate-900">Add tenant to this tenancy</h3>
              <button type="button" onClick={() => setJointOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={addJoint} className="space-y-3">
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Tenant name</label>
                <input data-testid="joint-name" required value={jointForm.name} onChange={(e) => setJointForm({ ...jointForm, name: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                  <input data-testid="joint-email" type="email" value={jointForm.email} onChange={(e) => setJointForm({ ...jointForm, email: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                  <input data-testid="joint-phone" value={jointForm.phone} onChange={(e) => setJointForm({ ...jointForm, phone: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <button type="submit" data-testid="save-joint-tenant" disabled={tenancyBusy}
                className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {tenancyBusy ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Add tenant
              </button>
            </form>
          </div>
        </div>
      )}

      {histOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setHistOpen(false)}>
          <div data-testid="history-detail-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-1">
              <div>
                <h3 className="font-display text-xl font-bold text-slate-900">{histEntry?.tenant}</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {histEntry?.tenancy_start ? fmtDate(histEntry.tenancy_start) : "—"} → {histEntry?.tenancy_end ? fmtDate(histEntry.tenancy_end) : "—"} · {gbp(histEntry?.rent_pcm)}/mo
                </p>
                <p className="text-xs text-slate-400">{[histEntry?.tenant_email, histEntry?.tenant_phone].filter(Boolean).join(" · ")}</p>
              </div>
              <button type="button" onClick={() => setHistOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            {(histEntry?.additional_tenants || []).length > 0 && (
              <p className="text-xs text-slate-500 mt-2">Joint tenants: {histEntry.additional_tenants.map((t) => t.name).join(", ")}</p>
            )}

            {!histDetail ? <div className="py-10"><Loading /></div> : (
              <>
                <div className="mt-5">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><PiggyBank size={15} /> Rent payments <span className="text-xs font-normal text-slate-400">· {gbp(histDetail.total_paid)} total</span></p>
                  {histDetail.payments.length === 0 ? <p className="text-xs text-slate-400">No rent payments recorded during this tenancy.</p> : (
                    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="history-payments">
                      {histDetail.payments.map((p, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                          <span className="text-slate-600">{fmtDate(p.date)} · {p.description || "Rent"}</span>
                          <span className={`font-medium ${p.direction === "in" ? "text-emerald-700" : "text-red-600"}`}>{gbp(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-5">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><FolderOpen size={15} /> Documents</p>
                  {histDetail.documents.length === 0 ? <p className="text-xs text-slate-400">No documents uploaded during this tenancy.</p> : (
                    <div className="space-y-2" data-testid="history-documents">
                      {histDetail.documents.map((f) => (
                        <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer"
                          className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
                          <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0"><FileText size={16} /></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{f.label || f.original_filename}</p>
                            <p className="text-xs text-slate-400 truncate">{f.original_filename} · {fmtDate(f.date)}</p>
                          </div>
                          <Download size={15} className="text-slate-400" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

function Stat({ label, value, icon: Icon, small }) {
  return (
    <div className="p-4">
      <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">{label}</p>
      <p className={`font-display font-bold text-slate-900 flex items-center gap-1.5 ${small ? "text-sm truncate" : "text-lg tabular-nums"}`}>
        {Icon && <Icon size={16} className="text-slate-400" />} {value}
      </p>
    </div>
  );
}
